import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ComponentComplete } from "node_js/models/business/lab/component-complete";
import { GadgetComplete } from "node_js/models/business/lab/gadget-complete";
import { LabComplete } from "node_js/models/business/lab/lab-complete";
import { RoleEnum } from "node_js/models/enums/role";
import { GadgetUser } from "node_js/models/tables/gadget_user";
import { LabUser } from "node_js/models/tables/lab_user";
import { UserAuthService } from "./user-auth.service";
import { LabService } from "./lab.service";
import { GadgetUserComplete } from "node_js/models/business/lab/gadget-user-complete";
import { connLink, presentSnackBar } from "src/global";
import { FailedResponse } from "node_js/models/failed-response.model";
import { SnackBarColor } from "node_js/models/enums/snackbar-color";
import { Subject } from "rxjs";
import { MainSliderSection } from "../pages/dashboard/dashboard.page";
import { StateService } from "./state.service";
import { FileUploadService } from "./file-upload.service";
import { ioGadgetSettings } from "node_js/models/business/lab/io-gadget-settings";


@Injectable({
  providedIn: 'root'
})
export class GadgetService {

  onCurrentSelectedGadgetUsersCreated: Subject<any>;
  currentSelectedGadgetUserSettingsChanged: Subject<any>;

  constructor(private http: HttpClient, private matSnackBar: MatSnackBar,
    private labService: LabService, private stateService: StateService, private fileUploadService: FileUploadService) {
    this.onCurrentSelectedGadgetUsersCreated = new Subject();
    this.currentSelectedGadgetUserSettingsChanged = new Subject();
  }

  //When getting data from a websocket, for some reason the buffer is already an array buffer so we skip 
  //the conversion. I'm just winging it at this point...
  convertGadgetImageBuffersToBlob(gadgetComplete: GadgetComplete, skipArrayBufferConversion: boolean = false) {
    if (gadgetComplete.iconFileUploadComplete) {
      if (gadgetComplete.iconFileUploadComplete.buffer) {
        this.fileUploadService.convertFileUploadCompleteToBlob(gadgetComplete.iconFileUploadComplete, skipArrayBufferConversion);
      } else if (gadgetComplete.iconFileUploadComplete.fileUpload?.filePathCode) {
        // Fallback to remote URL
        let filePathCode = gadgetComplete.iconFileUploadComplete.fileUpload.filePathCode;
        gadgetComplete.iconFileUploadComplete.srcUrl = `${connLink}/getFileFromGetRequest/${filePathCode}`;
      }
    }
  }

  upsertGadgetUsersCompleteLocally(newGadgetUsersComplete: GadgetUserComplete[], userAuthService: UserAuthService) {
    if (!newGadgetUsersComplete || newGadgetUsersComplete.length === 0) return;

    // We process each user individually (or grouped by gadget to be more efficient, but let's keep it simple)
    // Actually, finding the gadget for every user is inefficient if they are all for the same gadget.
    // But premature optimization is the root of all evil. Optimizing slightly: Group by GadgetID.

    // Efficiently updating local state
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];
      if (!labComplete.gadgetsComplete) continue;

      for (let j = 0; j < labComplete.gadgetsComplete.length; j++) {
        let gadgetComplete = labComplete.gadgetsComplete[j];

        // Check if any of the new users belong to this gadget
        // Optimization: Filter the input list for this gadget
        let usersForThisGadget = newGadgetUsersComplete.filter(u => u.gadgetUser.gadgetId == gadgetComplete.gadget.id);

        if (usersForThisGadget.length > 0) {
          // Upsert users for this gadget
          for (let k = 0; k < usersForThisGadget.length; k++) {
            let newUser = usersForThisGadget[k];
            let existingIndex = -1;

            if (!gadgetComplete.gadgetUsersComplete) gadgetComplete.gadgetUsersComplete = [];

            for (let l = 0; l < gadgetComplete.gadgetUsersComplete.length; l++) {
              if (gadgetComplete.gadgetUsersComplete[l].gadgetUser.userId == newUser.gadgetUser.userId) {
                existingIndex = l;
                break;
              }
            }

            if (existingIndex > -1) {
              // Update
              gadgetComplete.gadgetUsersComplete[existingIndex] = newUser;
            } else {
              // Append
              gadgetComplete.gadgetUsersComplete.push(newUser);
            }
          }

          // Update current user reference if needed
          this.setGadgetCompleteCurrentUserLocally(gadgetComplete, userAuthService);

          // Trigger update if this is the selected gadget
          if (this.labService.currentSelectedGadget && this.labService.currentSelectedGadget.gadget.id == gadgetComplete.gadget.id) {
            this.currentSelectedGadgetUserSettingsChanged.next(null);
          }
        }
      }
    }
  }



  canUserEditOtherGadgetUser(personalLabUser: LabUser, otherLabUser: LabUser, personalGadgetUser: GadgetUser, otherGadgetUser: GadgetUser, canEditOtherSuperAdminUser: boolean = true, canEditIfUserIdsMatch: boolean = true) {
    let pass = false;
    let personsalGadgetRoleId = personalGadgetUser.roleId;
    let otherGadgetRoleId = otherGadgetUser.roleId;

    //Pass if we can are super admin and the other user is not super admin, or we're allowed to edit other super admin
    if (this.labService.canUserEditOtherLabUser(personalLabUser, otherLabUser, canEditIfUserIdsMatch)) {
      //console.log("pass 1")
      pass = true;
    }
    //Pass if we have higher gadget role AND are at least ADMIN
    else if (personsalGadgetRoleId <= RoleEnum.ADMIN && personsalGadgetRoleId < otherGadgetRoleId) {
      //console.log("pass 2")
      pass = true;
    }
    //Pass if we are editing ourselves and are admin
    else if (personsalGadgetRoleId <= RoleEnum.ADMIN && canEditIfUserIdsMatch && personalGadgetUser.userId == otherGadgetUser.userId) {
      //console.log("pass 3")
      pass = true;
    }

    // Explicitly allow Lab Owner/Admin to edit Gadget Owner (and themselves)
    // We force 'true' for canEditIfUserIdsMatch because Lab Admins should be able to edit themselves in this context (reclaiming ownership)
    // But we restrict this override to Lab Admins only to prevent regular users from editing themselves
    else if (personalLabUser.roleId <= RoleEnum.ADMIN && this.labService.canUserEditOtherLabUser(personalLabUser, otherLabUser, true)) {
      // This block duplicates the first check but emphasizes the intent: Lab Hierarchy Wins.
      // Specifically, if I am Lab Owner, I can edit ANYONE in the lab, including Gadget Owners.
      pass = true;
    }

    return pass;
  }

  deleteComponent(userAuthService: UserAuthService, gadgetId: string, componentId: string) {
    return new Promise<void>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/deleteComponent", { uid: userAuthService.currentUserComplete.user.authUid, idToken, gadgetId, componentId }).subscribe(
        (response: any) => {

          console.log(response);
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  deleteComponentLocally(componentId: string, userAuthService: UserAuthService) {
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];

      for (let j = 0; j < labComplete.gadgetsComplete.length; j++) {
        let gadgetComplete = labComplete.gadgetsComplete[j];

        for (let k = 0; k < gadgetComplete.componentsComplete.length; k++) {
          let componentComplete = gadgetComplete.componentsComplete[k];
          if (componentComplete.component.id == componentId) {
            gadgetComplete.componentsComplete.splice(k, 1);
            if (this.labService.currentSelectedComponent.component.id == componentId) {
              this.labService.currentSelectedComponent = null;
              this.stateService.triggerMainSliderSectionObservable(MainSliderSection.GADGET, true);
            }
            return;
          }
        }
      }
    }
  }

  deleteGadgetLocally(gadgetId: string, userAuthService: UserAuthService) {
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];

      for (let j = 0; j < labComplete.gadgetsComplete.length; j++) {
        let gadgetComplete = labComplete.gadgetsComplete[j];

        if (gadgetComplete.gadget.id == gadgetId) {
          labComplete.gadgetsComplete.splice(j, 1);
          if (this.labService.currentSelectedGadget.gadget.id == gadgetId) {
            this.labService.currentSelectedComponent = null;
            this.labService.currentSelectedGadget = null;
            this.stateService.triggerMainSliderSectionObservable(MainSliderSection.LAB, true);
          }
          return;
        }
      }
    }
  }

  deleteGadget(userAuthService: UserAuthService, gadgetId: string) {
    return new Promise<void>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/deleteGadget", { uid: userAuthService.currentUserComplete.user.authUid, idToken, gadgetId }).subscribe(
        (response: any) => {

          console.log(response);
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  updateGadgetUserSettings(userAuthService: UserAuthService, gadgetId: string, gadgetUsersComplete: GadgetUserComplete[]) {
    return new Promise<GadgetUserComplete[]>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/updateGadgetUserSettings", { uid: userAuthService.currentUserComplete.user.authUid, idToken, gadgetId, gadgetUsersComplete }).subscribe(
        (response: any) => {

          console.log(response);
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  addCreatedGadgetUsersLocally(updatedGadgetUsersComplete: GadgetUserComplete[], userAuthService: UserAuthService) {
    let addedGadgetUsersComplete: GadgetUserComplete[] = [];
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];
      for (let j = 0; j < labComplete.gadgetsComplete.length; j++) {
        let gadgetComplete = labComplete.gadgetsComplete[j];


        for (let k = 0; k < updatedGadgetUsersComplete.length; k++) {
          let updatedGadgetUserComplete = updatedGadgetUsersComplete[k];
          if (updatedGadgetUserComplete.gadgetUser.gadgetId == gadgetComplete.gadget.id) {
            let foundUser = false;
            for (let l = 0; l < gadgetComplete.gadgetUsersComplete.length; l++) {
              let gadgetUserComplete = gadgetComplete.gadgetUsersComplete[l];
              if (updatedGadgetUserComplete.gadgetUser.userId == gadgetUserComplete.gadgetUser.userId) {
                foundUser = true;
                break;
              }
            }
            if (!foundUser) {
              if (this.labService.currentSelectedGadget && gadgetComplete.gadget.id == this.labService.currentSelectedGadget.gadget.id) {
                addedGadgetUsersComplete.push(updatedGadgetUserComplete);
              }
              gadgetComplete.gadgetUsersComplete.push(updatedGadgetUserComplete);
            }
            updatedGadgetUsersComplete.splice(k, 1);
            k--;
          }

        }

        // Ensure currentGadgetUserComplete is properly set after adding new users
        this.setGadgetCompleteCurrentUserLocally(gadgetComplete, userAuthService);
      }
    }
    this.onCurrentSelectedGadgetUsersCreated.next(addedGadgetUsersComplete);
  }

  createGadgetCompleteLocally(updatedGadgetComplete: GadgetComplete, userAuthService: UserAuthService) {
    console.log('[GadgetService] createGadgetCompleteLocally called for gadget:', updatedGadgetComplete.gadget.id);

    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];
      if (labComplete.lab.id == updatedGadgetComplete.gadget.labId) {
        let existingGadgetIndex = -1;
        for (let j = 0; j < labComplete.gadgetsComplete.length; j++) {
          let gadgetComplete = labComplete.gadgetsComplete[j];
          if (gadgetComplete.gadget.id == updatedGadgetComplete.gadget.id) {
            existingGadgetIndex = j;
            break;
          }
        }

        if (existingGadgetIndex >= 0) {
          // Gadget already exists - update it instead of creating a duplicate
          console.log('[GadgetService] Updating existing gadget instead of creating duplicate:', updatedGadgetComplete.gadget.id);

          // Ensure the gadget's image data is properly processed
          if (updatedGadgetComplete.iconFileUploadComplete && updatedGadgetComplete.iconFileUploadComplete.buffer) {
            this.convertGadgetImageBuffersToBlob(updatedGadgetComplete, true);
          }

          // Update the existing gadget with new data
          this.setGadgetCompleteCurrentUserLocally(updatedGadgetComplete, userAuthService);
          labComplete.gadgetsComplete[existingGadgetIndex] = updatedGadgetComplete;
        } else {
          // New gadget - add it
          console.log('[GadgetService] Adding new gadget:', updatedGadgetComplete.gadget.id);

          // Ensure the gadget's image data is properly processed before adding it
          if (updatedGadgetComplete.iconFileUploadComplete && updatedGadgetComplete.iconFileUploadComplete.buffer) {
            this.convertGadgetImageBuffersToBlob(updatedGadgetComplete, true);
          }

          this.setGadgetCompleteCurrentUserLocally(updatedGadgetComplete, userAuthService);
          labComplete.gadgetsComplete.push(updatedGadgetComplete);
        }
      }
    }
  }

  updateGadgetSettingsLocally(ioGadgetSettings: ioGadgetSettings, userAuthService: UserAuthService) {
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];

      for (let j = 0; j < labComplete.gadgetsComplete.length; j++) {
        let gadgetComplete = labComplete.gadgetsComplete[j];
        if (gadgetComplete.gadget.id == ioGadgetSettings.gadget.id) {

          if (ioGadgetSettings.iconFileUploadComplete) {
            // If we have a new upload, process it
            if (ioGadgetSettings.iconFileUploadComplete.srcUrl || ioGadgetSettings.iconFileUploadComplete.buffer) {
              // Revoke old URL safely
              if (gadgetComplete.iconFileUploadComplete?.srcUrl && typeof gadgetComplete.iconFileUploadComplete.srcUrl === 'string' && gadgetComplete.iconFileUploadComplete.srcUrl.startsWith('blob:')) {
                URL.revokeObjectURL(gadgetComplete.iconFileUploadComplete.srcUrl);
              }

              // Assign new object
              gadgetComplete.iconFileUploadComplete = ioGadgetSettings.iconFileUploadComplete;

              // Validate buffer exists before converting
              if (gadgetComplete.iconFileUploadComplete.buffer) {
                this.fileUploadService.convertFileUploadCompleteToBlob(gadgetComplete.iconFileUploadComplete, true);
              }

              gadgetComplete.gadget.iconFileUploadId = ioGadgetSettings.iconFileUploadComplete.fileUpload.id;
              gadgetComplete.gadget.curatedIconUrl = null;
            } else if (!ioGadgetSettings.iconFileUploadComplete.fileUpload) {
              // Icon removed
              gadgetComplete.iconFileUploadComplete = null;
              gadgetComplete.gadget.iconFileUploadId = null;
              // If curated icon is set in the update, use it
              gadgetComplete.gadget.curatedIconUrl = ioGadgetSettings.gadget.curatedIconUrl;
            }
          }

          // Update gadget properties
          Object.assign(gadgetComplete.gadget, ioGadgetSettings.gadget);
          gadgetComplete.gadget = { ...gadgetComplete.gadget, ...ioGadgetSettings.gadget };

          this.convertGadgetImageBuffersToBlob(gadgetComplete, true);
          gadgetComplete.gadgetLanguage = ioGadgetSettings.gadgetLanguage;
          this.labService.currentSelectedGadgetSettingsChanged.next(null);
          return;
        }
      }

    }
  }

  setGadgetCompleteCurrentUserLocally(gadgetComplete: GadgetComplete, userAuthService: UserAuthService) {
    // Ensure the gadget has gadgetUsersComplete array
    if (gadgetComplete.gadgetUsersComplete && gadgetComplete.gadgetUsersComplete.length > 0) {
      // Find and set currentGadgetUserComplete for the current user
      let foundCurrentUser = false;
      for (let i = 0; i < gadgetComplete.gadgetUsersComplete.length; i++) {
        let gadgetUserComplete = gadgetComplete.gadgetUsersComplete[i];
        if (gadgetUserComplete.gadgetUser.userId == userAuthService.currentUserComplete.user.id) {
          gadgetComplete.currentGadgetUserComplete = gadgetUserComplete;
          foundCurrentUser = true;
          break;
        }
      }

      // If currentGadgetUserComplete is still not set, set it to the first available user
      // This prevents undefined errors in the template
      if (!foundCurrentUser && gadgetComplete.gadgetUsersComplete.length > 0) {
        gadgetComplete.currentGadgetUserComplete = gadgetComplete.gadgetUsersComplete[0];
        console.warn('[GadgetService] currentGadgetUserComplete not found for current user, using first available user for gadget:', gadgetComplete.gadget.id);
      }
    } else {
      console.warn('[GadgetService] No gadget users found for gadget:', gadgetComplete.gadget.id);
    }
  }

  updateGadgetSettings(userAuthService: UserAuthService, gadgetComplete: GadgetComplete) {
    return new Promise<void>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();


      const formData = new FormData();
      formData.append("gadgetComplete", JSON.stringify(gadgetComplete));
      formData.append("uid", userAuthService.currentUserComplete.user.authUid);
      formData.append("idToken", idToken.toString());

      if (gadgetComplete.iconFileUploadComplete?.file) {
        formData.append("files", gadgetComplete.iconFileUploadComplete.file, gadgetComplete.iconFileUploadComplete.file.name);
      }


      this.http.post(connLink + "/updateGadgetSettings", formData, {}).subscribe(
        (response: any) => {
          console.log(response);
          this.updateGadgetSettingsLocally(response, userAuthService);
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  /**
   * Clear all gadget service data on logout to prevent stale data issues
   */
  clearAllData(): void {
    console.log('[GadgetService] Clearing all gadget service data');

    // Note: We don't clear the Subject subscriptions here as they need to persist
    // for the next login session. The app.component.ts manages subscription lifecycle.

    console.log('[GadgetService] Gadget service data cleared successfully');
  }
}
